import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Select,
  DatePicker,
  Row,
  Col,
  Typography,
  Alert,
  Input,
  Collapse,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  StopOutlined,
  PrinterOutlined,
  FileTextOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useReceiptVouchers, usePostReceiptVoucher, useCancelReceiptVoucher } from '@/hooks/useReceiptVoucher';
import { useCurrency } from '@/contexts/CurrencyContext';
import type { ReceiptVoucher } from '@/services/receiptVoucher.service';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;

const ReceiptVouchersPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([] as any);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [paymentModeFilter, setPaymentModeFilter] = useState<string>('');

  const filters = useMemo(
    () => ({
      search: searchText.trim() || undefined,
      from_date: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
      to_date: dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
      status: statusFilter || undefined,
      payment_mode: paymentModeFilter || undefined,
    }),
    [searchText, dateRange, statusFilter, paymentModeFilter]
  );
  const { data: list = [], isLoading } = useReceiptVouchers(filters);
  const postMutation = usePostReceiptVoucher();
  const cancelMutation = useCancelReceiptVoucher();

  const statusLabel = (status: string) => {
    const map: Record<string, string> = {
      Draft: tc('draft'),
      Posted: tc('posted'),
      Cancelled: tc('cancelled'),
    };
    return map[status] || status;
  };

  const receiptTypeLabel = (type: string) => {
    const map: Record<string, string> = {
      AgainstInvoice: t('receiptTypeAgainstInvoice'),
      DirectRevenue: t('receiptTypeDirectRevenue'),
      AdvanceFromCustomer: t('receiptTypeAdvanceFromCustomer'),
      OnAccount: t('receiptTypeOnAccount'),
      Other: t('receiptTypeOther'),
    };
    return map[type] || type;
  };

  const paymentModeLabel = (mode: string) => {
    const map: Record<string, string> = {
      Cash: t('modeCash'),
      Cheque: t('modeCheque'),
      BankTransfer: t('modeBankTransfer'),
      Card: t('modeCard'),
      Online: t('modeOnline'),
      Other: t('modeOther'),
    };
    return map[mode] || mode;
  };

  const applySearch = (value?: string) => {
    setSearchText((value ?? searchInput).trim());
  };

  const clearAdvanced = () => {
    setDateRange([] as any);
    setStatusFilter('');
    setPaymentModeFilter('');
  };

  const columns: ColumnsType<ReceiptVoucher> = [
    {
      title: t('receiptNo'),
      dataIndex: 'receipt_number',
      key: 'receipt_number',
      fixed: 'left',
      width: 130,
      render: (val: string, record: ReceiptVoucher) => (
        <a onClick={() => navigate(`/fin/receipt-vouchers/${record.id}`)}>{val ?? '—'}</a>
      ),
    },
    {
      title: t('date'),
      dataIndex: 'receipt_date',
      key: 'receipt_date',
      width: 110,
      render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY') : '-'),
    },
    {
      title: t('customer'),
      key: 'customer',
      width: 180,
      render: (_, r) => (r as any).customer?.customer_name ?? r.received_from_name ?? '-',
    },
    {
      title: t('type'),
      dataIndex: 'receipt_type',
      key: 'receipt_type',
      width: 140,
      render: (type: string) => receiptTypeLabel(type),
    },
    {
      title: t('amount'),
      dataIndex: 'amount',
      key: 'amount',
      width: 110,
      align: 'right',
      render: (v: number) => formatCurrency(Number(v)),
    },
    {
      title: t('payment'),
      dataIndex: 'payment_mode',
      key: 'payment_mode',
      width: 110,
      render: (mode: string) => paymentModeLabel(mode),
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: string) => {
        const color = s === 'Posted' ? 'green' : s === 'Cancelled' ? 'red' : 'default';
        return <Tag color={color}>{statusLabel(s)}</Tag>;
      },
    },
    {
      title: t('actions'),
      key: 'actions',
      fixed: 'right',
      width: 280,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/fin/receipt-vouchers/${record.id}`)}
          >
            {tc('view')}
          </Button>
          {record.status === 'Draft' && (
            <>
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/fin/receipt-vouchers/${record.id}/edit`)}
              >
                {tc('edit')}
              </Button>
              <Button
                type="link"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => postMutation.mutate(record.id)}
                loading={postMutation.isPending}
              >
                {tc('post')}
              </Button>
            </>
          )}
          {record.status === 'Posted' && (
            <Button
              type="link"
              size="small"
              danger
              icon={<StopOutlined />}
              onClick={() => cancelMutation.mutate(record.id)}
              loading={cancelMutation.isPending}
            >
              {tc('cancel')}
            </Button>
          )}
          {record.status === 'Posted' && (
            <>
              <Button
                type="link"
                size="small"
                icon={<PrinterOutlined />}
                onClick={() => navigate(`/fin/receipt-vouchers/${record.id}?print=receipt`)}
              >
                {t('receipt')}
              </Button>
              <Button
                type="link"
                size="small"
                icon={<FileTextOutlined />}
                onClick={() => navigate(`/fin/receipt-vouchers/${record.id}?print=voucher`)}
              >
                {t('voucher')}
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            {t('receiptVouchers')}
          </Title>
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 16, maxWidth: 720 }}
            message={t('receiptVoucherRegister')}
            description={t('searchReceiptHint')}
          />
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/fin/receipt-vouchers/new')}>
            {t('newReceiptVoucher')}
          </Button>
        </Col>
      </Row>
      <Card>
        <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
          <Search
            placeholder={t('searchReceiptPlaceholder')}
            allowClear
            enterButton={<SearchOutlined />}
            value={searchInput}
            onChange={(e) => {
              const v = e.target.value;
              setSearchInput(v);
              if (!v.trim()) setSearchText('');
            }}
            onSearch={applySearch}
            style={{ maxWidth: 720 }}
          />
          <Collapse
            ghost
            items={[
              {
                key: 'advanced',
                label: <Text type="secondary">{t('advancedFilters')}</Text>,
                children: (
                  <Space wrap>
                    <RangePicker value={dateRange} onChange={(v) => setDateRange(v as any)} />
                    <Select
                      placeholder={t('filterStatus')}
                      allowClear
                      style={{ width: 120 }}
                      value={statusFilter || undefined}
                      onChange={(v) => setStatusFilter(v ?? '')}
                      options={[
                        { value: 'Draft', label: tc('draft') },
                        { value: 'Posted', label: tc('posted') },
                        { value: 'Cancelled', label: tc('cancelled') },
                      ]}
                    />
                    <Select
                      placeholder={t('filterPaymentMode')}
                      allowClear
                      style={{ width: 140 }}
                      value={paymentModeFilter || undefined}
                      onChange={(v) => setPaymentModeFilter(v ?? '')}
                      options={[
                        { value: 'Cash', label: t('modeCash') },
                        { value: 'Cheque', label: t('modeCheque') },
                        { value: 'BankTransfer', label: t('modeBankTransfer') },
                        { value: 'Card', label: t('modeCard') },
                        { value: 'Online', label: t('modeOnline') },
                        { value: 'Other', label: t('modeOther') },
                      ]}
                    />
                    <Button onClick={clearAdvanced}>{t('clearFilters')}</Button>
                  </Space>
                ),
              },
            ]}
          />
        </Space>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          loading={isLoading}
          sticky={{ offsetHeader: 0 }}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => t('totalVouchers', { count: total }),
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          scroll={{ x: 1100, y: 520 }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default ReceiptVouchersPage;
